import { useCallback } from 'react';
import { FieldErrors, FormProvider } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { DocumentWorkflowTemplateInfo } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { getFileReactQuery } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { MasterDocumentForm } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/MasterDocumentForm.tsx';
import { MasterDocumentFormDto } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/MasterDocumentForm/types.ts';

import { useEditMasterDocuments } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/EditMasterDocument/useEditMasterDocuments.ts';
import { EditFooter } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/components/EditMasterDocument/EditFooter.tsx';

interface Props {
	editContract: DocumentWorkflowTemplateInfo;
	onDeleteSuccess?: () => void;
}

export const EditMasterDocumentBody = ({ editContract, onDeleteSuccess }: Props) => {
	const tMipManage = useTranslationMipManage();
	const fileId = editContract.file?.id ?? null;

	const { data: templateFile } = useQuery({
		queryKey: [API_PATHS.FILE_ID_GET, { id: fileId }, 'placement-editor'],
		queryFn: async ({ signal }) => {
			if (!fileId) {
				return undefined;
			}
			const response = await getFileReactQuery({ id: fileId }, signal);
			return response ? await response.blob() : undefined;
		},
		enabled: !!fileId,
	});

	const {
		form,
		classifierOptions,
		onSubmit,
		onArchive,
		onDelete,
		onTogglePostDating,
		isSettingPostDating,
		isUpdating,
		isArchiving,
		isDeleting,
	} = useEditMasterDocuments({ editContract, onDeleteSuccess });

	const handleInvalid = useCallback(
		(errors: FieldErrors<MasterDocumentFormDto>) => {
			const firstFieldKey = Object.keys(errors)[0] as
				| keyof FieldErrors<MasterDocumentFormDto>
				| undefined;
			const firstMessage = firstFieldKey
				? (errors[firstFieldKey] as { message?: string } | undefined)?.message
				: undefined;
			showToastV2({
				state: 'error',
				title: firstMessage ?? tMipManage('master_documents.text.validation_error'),
			});
		},
		[tMipManage]
	);

	/**
	 * Rejects when validation stopped the write, rather than resolving as `handleSubmit` does.
	 * The editor modal saves through this and closes on success, so "the invalid handler ran"
	 * and "it was written" must not look the same from outside. `handleInvalid` has already
	 * said what was wrong.
	 */
	const saveDocument = useCallback(async () => {
		let written = false;
		await form.handleSubmit(async (values) => {
			await onSubmit(values);
			written = true;
		}, handleInvalid)();
		if (!written) {
			throw new Error('Master document was not saved: the form is invalid');
		}
	}, [form, onSubmit, handleInvalid]);

	const handleSave = useCallback(() => {
		// The toast from `handleInvalid`, or the mutation's own, is the report.
		void saveDocument().catch(() => undefined);
	}, [saveDocument]);

	return (
		<FormProvider {...form}>
			<MasterDocumentForm
				form={form}
				mode="edit"
				classifierOptions={classifierOptions}
				templateId={editContract.id}
				templateFile={templateFile}
				templateSourceFormat={editContract.sourceFormat}
				settingLocks={editContract.settingLocks}
				disabled={isUpdating || editContract.archived}
				onTogglePostDating={onTogglePostDating}
				isTogglingPostDating={isSettingPostDating}
				onSaveDocument={saveDocument}
				isSavingDocument={isUpdating}
				footerSlot={
					<EditFooter
						onSaveClick={handleSave}
						onArchive={onArchive}
						onDelete={onDelete}
						isUpdating={isUpdating}
						isArchiving={isArchiving}
						isDeleting={isDeleting}
						archived={editContract.archived}
						documentName={editContract.name}
					/>
				}
			/>
		</FormProvider>
	);
};
